import _ from 'lodash'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import { defaultDevices } from '@/constants/devices'

import { AuthUser } from '@/core/classes/Auth'
import customDeviceModel, { CreateCustomDeviceDto, CustomDevice } from '@/core/models/custom-device.model'

import {
  getCustomDevices as getCustomDevicesAction,
  getCustomDevicesSuccess as getCustomDevicesSuccessAction,
  getCustomDevicesFailure as getCustomDevicesFailureAction,
  createCustomDevice as createCustomDeviceAction,
  createCustomDeviceSuccess as createCustomDeviceSuccessAction,
  createCustomDeviceFailure as createCustomDeviceFailureAction,
  deleteCustomDevice as deleteCustomDeviceAction,
  deleteCustomDeviceSuccess as deleteCustomDeviceSuccessAction,
  deleteCustomDeviceFailure as deleteCustomDeviceFailureAction,
} from '@/redux/reducers/custom-device.reducer'

import { handleError } from '@/utils/errors'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

/**
 * Normalizes a device label/name for comparison by lowercasing and
 * collapsing punctuation and extra whitespace into single spaces.
 */
function normalizeDeviceLabel(s: string): string {
  return s.toLowerCase().replace(/[-_().!,]/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Returns true when the OS-reported device label is a plausible match for a
 * hardcoded device name. The check is intentionally lenient:
 *
 *  1. Direct substring (normalized): "sound blaster play 3" ⊂ "microphone (sound blaster play 3)"
 *  2. Reverse substring: the OS label is fully contained in the search name
 *  3. Token-based: every significant token (>1 char) from the search name
 *     appears somewhere in the OS label — handles "PCP-USB" → "pcp usb audio"
 */
function deviceLabelMatches(osLabel: string, searchName: string): boolean {
  const label = normalizeDeviceLabel(osLabel)
  const search = normalizeDeviceLabel(searchName)

  if (label.includes(search)) return true
  if (search.includes(label)) return true

  const tokens = search.split(' ').filter((t) => t.length > 1)
  return tokens.length > 0 && tokens.every((token) => label.includes(token))
}

export function* getCustomDevices() {
  try {
    const authUser: AuthUser | null = yield select(selectAuthUser)
    const customDevices: CustomDevice[] = yield call(customDeviceModel.getCustomDevices)

    // Request microphone+camera permission so enumerateDevices() returns labels.
    // Without this, browsers return empty labels and device matching fails.
    let tempStream: MediaStream | null = null
    try {
      tempStream = yield navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    } catch {
      // Permission denied or no device — continue with what enumerateDevices gives us
    }

    const mediaDevices: MediaDeviceInfo[] = yield navigator.mediaDevices.enumerateDevices()

    // Release the temporary stream now that we have labels
    if (tempStream) {
      tempStream.getTracks().forEach((t) => t.stop())
    }

    const defaultCustomDevices: CustomDevice[] = []
    defaultDevices.forEach((value) => {
      const mediaDevice = mediaDevices.find((mediaDevice) => deviceLabelMatches(mediaDevice.label, value.name))
      if (!!mediaDevice) {
        defaultCustomDevices.push({
          ...value,
          _id: mediaDevice.deviceId,
          device: { deviceId: mediaDevice.deviceId, name: mediaDevice.label, type: mediaDevice.kind },
          owner: authUser?._id || '',
        })
      }
    })
    yield put(
      getCustomDevicesSuccessAction(
        _.uniqBy(
          _.concat(
            customDevices.map((customDevice) => {
              const device = mediaDevices.find((mediaDevice) =>
                mediaDevice.label === customDevice.device.name || deviceLabelMatches(mediaDevice.label, customDevice.device.name)
              )
              return {
                ...customDevice,
                available: !!device,
                device: !device
                  ? customDevice.device
                  : {
                    deviceId: device.deviceId,
                    name: device.label,
                    type: device.kind,
                  },
              }
            }),
            defaultCustomDevices
          ),
          'device.deviceId'
        )
      )
    )
  } catch (exception) {
    console.warn(exception)
    yield put(getCustomDevicesFailureAction(handleError(exception)))
  }
}

function* createCustomDevice(action: PayloadAction<CreateCustomDeviceDto>) {
  try {
    const customDevice: CustomDevice = yield call(customDeviceModel.createCustomDevice, action.payload)
    yield put(createCustomDeviceSuccessAction({ ...customDevice, available: true }))
  } catch (exception) {
    console.warn(exception)
    yield put(createCustomDeviceFailureAction(handleError(exception)))
  }
}

function* deleteCustomDevice(action: PayloadAction<string>) {
  try {
    const customDeviceId = action.payload
    yield call(customDeviceModel.deleteCustomDevice, customDeviceId)
    yield put(deleteCustomDeviceSuccessAction(customDeviceId))
  } catch (exception) {
    console.warn(exception)
    yield put(deleteCustomDeviceFailureAction(handleError(exception)))
  }
}

const sagas = [
  takeLatest(getCustomDevicesAction.type, getCustomDevices),
  takeLatest(createCustomDeviceAction.type, createCustomDevice),
  takeLatest(deleteCustomDeviceAction.type, deleteCustomDevice),
]

export default sagas
